import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Spinner } from '../components/Spinner';
import { MetricCard } from '../components/Card';
import { Sparkline } from '../components/Sparkline';
import { EmptyState } from '../components/EmptyState';
import { IconChart, IconLink, IconCampaign, IconPlus } from '../components/Icons';
import type { Campaign, LinkAnalytics, ShortLink } from '@shared/types';
import { formatNumber, formatRelative } from '../lib/format';

interface DashboardData {
  metrics: { links: number; clicks: number; campaigns: number };
  recentLinks: ShortLink[];
  campaigns: Campaign[];
  analytics: LinkAnalytics;
}

export function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<DashboardData>('/api/dashboard')
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  if (error)
    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 px-4 py-3 text-sm">
        {error}
      </div>
    );
  if (!data)
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size={28} />
      </div>
    );

  const { metrics, recentLinks, campaigns, analytics } = data;

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Overview</h1>
          <p className="text-sm text-ink-500 dark:text-ink-400">
            Last 14 days at a glance.
          </p>
        </div>
        <Link to="/app/links/new" className="btn-primary">
          <IconPlus />
          New short link
        </Link>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <MetricCard label="Links" value={formatNumber(metrics.links)} icon={<IconLink />} />
        <MetricCard label="Clicks" value={formatNumber(metrics.clicks)} icon={<IconChart />} />
        <MetricCard
          label="Campaigns"
          value={formatNumber(metrics.campaigns)}
          icon={<IconCampaign />}
        />
      </div>

      <section className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-ink-900 dark:text-ink-50">Click activity</h2>
          <span className="text-xs text-ink-500 dark:text-ink-400">
            {analytics.totals.total} successful · {analytics.totals.unique} unique
          </span>
        </div>
        <Sparkline
          data={analytics.timeseries.map((d) => ({ date: d.date, total: d.totalClicks }))}
          showAxis
          height={140}
        />
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Top countries</h2>
          </div>
          {analytics.topCountries.length ? (
            <ul className="space-y-2">
              {analytics.topCountries.map((c) => (
                <li
                  key={c.key}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="font-medium">{c.key}</span>
                  <span className="text-ink-500 dark:text-ink-400">
                    {formatNumber(c.count)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-500 dark:text-ink-400">No country data yet.</p>
          )}
        </div>
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Top referrers</h2>
          </div>
          {analytics.topReferrers.length ? (
            <ul className="space-y-2">
              {analytics.topReferrers.map((c) => (
                <li
                  key={c.key}
                  className="flex items-center justify-between text-sm"
                >
                  <span
                    className="font-medium truncate max-w-xs"
                    title={c.key}
                  >
                    {c.key}
                  </span>
                  <span className="text-ink-500 dark:text-ink-400">
                    {formatNumber(c.count)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-500 dark:text-ink-400">
              No referrer data yet.
            </p>
          )}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Recent links</h2>
          <Link
            to="/app/links"
            className="text-sm text-brand-600 dark:text-brand-300 font-medium"
          >
            View all
          </Link>
        </div>
        {recentLinks.length === 0 ? (
          <EmptyState
            icon={<IconLink />}
            title="No links yet"
            description="Create your first short link to start collecting analytics."
            action={
              <Link to="/app/links/new" className="btn-primary">
                <IconPlus />
                New link
              </Link>
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Destination</th>
                  <th>Clicks</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {recentLinks.map((l) => (
                  <tr key={l.id}>
                    <td className="font-mono text-xs">
                      <Link
                        to={`/app/links/${l.id}`}
                        className="text-brand-600 dark:text-brand-300 hover:underline"
                      >
                        {l.code}
                      </Link>
                    </td>
                    <td className="truncate max-w-md">{l.destinationUrl}</td>
                    <td>{formatNumber(l.clickCount)}</td>
                    <td className="text-ink-500 dark:text-ink-400">
                      {formatRelative(l.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {campaigns.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Recent campaigns</h2>
            <Link
              to="/app/campaigns"
              className="text-sm text-brand-600 dark:text-brand-300 font-medium"
            >
              View all
            </Link>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {campaigns.map((c) => (
              <Link
                key={c.id}
                to={`/app/campaigns/${c.id}`}
                className="card p-5 hover:border-brand-300 dark:hover:border-brand-700 transition"
              >
                <div className="font-semibold">{c.name}</div>
                <div className="text-xs text-ink-500 dark:text-ink-400 mt-1">
                  Created {formatRelative(c.createdAt)}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
